import Highcharts from 'highcharts/highstock';
import AMApp from '../../global/app';

/**
 * FondsRecommendation for lbbw-am.de
 *
 * @author Florian Gutwald
 * Converted to Plain JS
 */
export default class FondsRecommendation {
  constructor(element, options = {}) {
    this.element = element;

    const defaults = {
      routeZusammensetzung: AMApp.langGetParam + 'eID=fonds&function=zusammensetzung&fondsId=',
      pieOptions: {
        chart: {
          height: 400
        },
        legend: {
          align: 'center',
          layout: 'horizontal',
          verticalAlign: 'bottom',
          legendX: 0,
          legendY: 0
        },
        series: {
          size: '60%',
          innerSize: '80%',
          centerTop: '40%',
          centerLeft: '50%'
        }
      }
    };

    this.options = this._mergeDeep(defaults, options);
    this.init();
  }

  /**
   * Hilfsmethode für Deep Merge
   */
  _mergeDeep(target, source) {
    for (const key in source) {
      if (source[key] instanceof Object && key in target) {
        Object.assign(source[key], this._mergeDeep(target[key], source[key]));
      }
    }
    Object.assign(target || {}, source);
    return target;
  }

  /**
   * Init method
   */
  init() {
    this.getChartParameter();
  }

  /**
   * Get chart parameter
   */
  getChartParameter() {
    // Das Original suchte global nach #fondsrecommendation_chart.
    // Falls es mehrere Instanzen gibt, wäre this.element.querySelector besser.
    const pieElement = document.getElementById('fondsrecommendation_chart');
    if (!pieElement) return;

    const fondsId = pieElement.dataset.fondsid;
    const type = pieElement.dataset.chartType || 'pie';
    const container = pieElement.id;

    this.chartKey = pieElement.dataset.zusammensetzung;

    this.getChartData(fondsId, container, type);
  }

  /**
   * Get chart data (Ersatz für $.ajax)
   */
  async getChartData(fondsId, container, type) {
    const url = AMApp.basePath + this.options.routeZusammensetzung + fondsId;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      if (result && result[this.chartKey]) {
        this.prepareChartData(result[this.chartKey], container, type);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Chart-Daten:', error);
    }
  }

  /**
   * Prepare chartData for chart rendering
   */
  prepareChartData(chartData, container, type) {
    if (chartData) {
      const title = chartData.title || '';
      const data = chartData.data || {};

      const categories = [];
      const series = {
        data: []
      };

      // Iteration über das Daten-Objekt (Ersatz für $.each)
      Object.entries(data).forEach(([category, value]) => {
        categories.push(category);
        series.data.push({
          name: category,
          y: value
        });
      });

      this.initChart(container, type, categories, series, title);
    }
  }

  /**
   * Init charts
   */
  initChart(container, type, categories, series, title) {
    switch (type) {
      case 'pie':
        this.createChartPie(container, series.data, title);
        break;
      case 'bar':
        this.createChartBar(container, series, title, categories);
        break;
    }
  }

  /**
   * Chart type 'pie'
   */
  createChartPie(container, data, title) {
    const options = {
      styledMode: false,
      chart: {
        renderTo: container,
        type: 'pie',
        marginLeft: 0,
        height: this.options.pieOptions.chart.height
      },
      credits: { enabled: false },
      title: { text: '' },
      legend: {
        title: { text: `<b>${title}</b>` },
        align: this.options.pieOptions.legend.align,
        layout: this.options.pieOptions.legend.layout,
        verticalAlign: this.options.pieOptions.legend.verticalAlign,
        floating: true,
        borderWidth: 0,
        x: this.options.pieOptions.legend.legendX,
        y: this.options.pieOptions.legend.legendY
      },
      tooltip: {
        formatter: function () {
          return `<b>${this.point.name}: </b>${Highcharts.numberFormat(this.point.y * 100, 1)} %`;
        }
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            formatter: function () {
              return Highcharts.numberFormat(this.y * 100, 1) + ' %';
            },
            distance: 15
          }
        }
      },
      series: [{
        data: data,
        center: [
          this.options.pieOptions.series.centerLeft,
          this.options.pieOptions.series.centerTop
        ],
        size: this.options.pieOptions.series.size,
        innerSize: this.options.pieOptions.series.innerSize,
        showInLegend: true
      }]
    };

    new Highcharts.Chart(options);
  }

  /**
   * Chart type 'bar'
   */
  createChartBar(container, data, title, categories) {
    const options = {
      exporting: { enabled: false },
      chart: {
        renderTo: container,
        type: 'bar'
      },
      title: { text: title },
      tooltip: {
        enabled: true,
        formatter: function () {
          return `<b>${Highcharts.numberFormat(this.y * 100, 2, ',')} %</b>`;
        }
      },
      plotOptions: {
        series: {
          stacking: 'normal',
          animation: true
        },
        bar: {
          pointWidth: 12,
          dataLabels: {
            enabled: true,
            align: 'left',
            x: 1,
            y: -13,
            formatter: function () {
              return `${this.x}: <b>${Highcharts.numberFormat(this.y * 100, 2, ',')} %</b>`;
            },
            style: { fontSize: '15px', fontWeight: 'normal' }
          }
        }
      },
      xAxis: {
        categories: categories,
        labels: { enabled: false }
      },
      yAxis: {
        title: { text: null },
        labels: {
          formatter: function () {
            return Highcharts.numberFormat(this.value * 100, 1) + ' %';
          }
        }
      },
      legend: { enabled: false },
      series: [data]
    };

    new Highcharts.Chart(options);
  }
}
